import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  TABLE_FILL_MIN_CONTAINER,
  TABLE_HEADER_RESERVE,
  TABLE_MIN_SCROLL_Y,
  TABLE_PAGINATION_RESERVE,
} from "../constants";

/** scroll.y 切换滞后 px，避免边界高度在 ResizeObserver 中反复横跳 */
const SCROLL_Y_HYSTERESIS = 8;

/** useFillHeightScrollY 入参 */
export interface UseFillHeightScrollYOptions {
  /** 是否启用撑满父容器剩余高度 */
  enabled: boolean;
  /** 是否展示分页（影响底部预留） */
  showPagination: boolean;
  /** 额外底部预留 px */
  bottomReserve?: number;
  /** scroll.y 下限 */
  minScrollY?: number;
  /** 数据/加载变化时触发重测 */
  remeasureKey?: unknown;
}

/**
 * 从 flex 父容器计算表格区域可用高度（减去兄弟节点占用）
 */
const resolveContainerHeight = (el: HTMLElement): number => {
  const parent = el.parentElement;
  if (!parent) return el.clientHeight;
  let siblingHeight = 0;
  for (const child of parent.children) {
    if (child === el) continue;
    siblingHeight += (child as HTMLElement).offsetHeight;
  }
  const style = getComputedStyle(parent);
  const parentPadding = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
  const parentGap = parseFloat(style.rowGap || style.gap || "0") || 0;
  const siblingCount = Math.max(parent.children.length - 1, 0);
  const parentAvailable =
    parent.clientHeight - parentPadding - siblingHeight - parentGap * siblingCount;
  return Math.max(el.clientHeight, parentAvailable);
};

/**
 * 读取表体内容实际高度（scrollHeight，不受 scroll.y 裁剪影响）
 */
const measureBodyContentHeight = (el: HTMLElement): number => {
  const tbody = el.querySelector(".ant-table-tbody") as HTMLElement | null;
  if (tbody) return tbody.scrollHeight;
  const body = el.querySelector(".ant-table-body") as HTMLElement | null;
  return body?.scrollHeight ?? 0;
};

/**
 * 通过 ResizeObserver 测量容器高度，仅在内容溢出时返回 scroll.y
 * 需父级 flex 列布局且 min-h-0，否则容器高度不足时不设置 scroll.y
 */
export const useFillHeightScrollY = ({
  enabled,
  showPagination,
  bottomReserve = 0,
  minScrollY = TABLE_MIN_SCROLL_Y,
  remeasureKey,
}: UseFillHeightScrollYOptions) => {
  /** 撑满高度时的外层容器 ref */
  const wrapperRef = useRef<HTMLDivElement>(null);
  /** 动态计算的 scroll.y；undefined 表示内容未溢出、不启用纵向滚动 */
  const [scrollY, setScrollY] = useState<number | undefined>();
  /** ResizeObserver 合并到同一帧，避免连续 setState */
  const rafIdRef = useRef<number>();

  /** 根据容器与表头/分页占用重算 scroll.y */
  const updateScrollY = useCallback(() => {
    if (!enabled) return;
    const el = wrapperRef.current;
    if (!el) return;
    const containerHeight = resolveContainerHeight(el);
    if (containerHeight < TABLE_FILL_MIN_CONTAINER) return;
    const thead = el.querySelector(".ant-table-thead");
    const theadHeight = thead?.getBoundingClientRect().height ?? TABLE_HEADER_RESERVE;
    const paginationEl = el.querySelector(".ant-pagination");
    const paginationHeight = showPagination
      ? (paginationEl?.getBoundingClientRect().height ?? TABLE_PAGINATION_RESERVE)
      : 0;
    const maxBodyHeight = Math.max(
      containerHeight - theadHeight - paginationHeight - bottomReserve,
      minScrollY,
    );
    const contentHeight = measureBodyContentHeight(el);
    // 仅内容超出可用高度时启用 scroll.y；带滞后避免 scroll.y 开关抖动
    setScrollY((prev) => {
      const needsScroll =
        prev !== undefined
          ? contentHeight > maxBodyHeight - SCROLL_Y_HYSTERESIS
          : contentHeight > maxBodyHeight;
      const next = needsScroll ? maxBodyHeight : undefined;
      return prev === next ? prev : next;
    });
  }, [enabled, showPagination, bottomReserve, minScrollY]);

  /** 合并同一帧内的多次 ResizeObserver 回调 */
  const scheduleUpdateScrollY = useCallback(() => {
    if (rafIdRef.current !== undefined) {
      window.cancelAnimationFrame(rafIdRef.current);
    }
    rafIdRef.current = window.requestAnimationFrame(() => {
      rafIdRef.current = undefined;
      updateScrollY();
    });
  }, [updateScrollY]);

  useLayoutEffect(() => {
    if (!enabled) {
      setScrollY(undefined);
      return;
    }
    updateScrollY();
    const timer = window.requestAnimationFrame(updateScrollY);
    return () => {
      window.cancelAnimationFrame(timer);
      if (rafIdRef.current !== undefined) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = undefined;
      }
    };
  }, [enabled, updateScrollY, remeasureKey]);

  useEffect(() => {
    if (!enabled) return;
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => scheduleUpdateScrollY());
    observer.observe(el);
    const parent = el.parentElement;
    if (parent) observer.observe(parent);
    const tbody = el.querySelector(".ant-table-tbody");
    if (tbody) observer.observe(tbody);
    return () => observer.disconnect();
  }, [enabled, scheduleUpdateScrollY]);

  return { wrapperRef, scrollY };
};
